import { useMemo } from 'react';

import { Stack, Typography } from '@mui/material';

import ResidentAvatar, { ResidentAvatarProps } from 'src/component-library/ResidentAvatar';
import HospiceChip from 'src/components/fragments/HospiceChip';
import ResuscitationChip from 'src/components/fragments/ResuscitationChip';
import { CareTypeLabelsAbbreviated } from 'src/constants/CareTypes';
import { HospiceStatus } from 'src/constants/HospiceStatus';
import {
    ResuscitationStrategies,
    ResuscitationStrategyType,
} from 'src/constants/ResuscitationStrategies';
import useUserPermissions from 'src/hooks/useUserPermissions';
import { Unit } from 'src/store/sageAdminApi';
import { isHospice } from 'src/utils/HospiceUtils';
import { getResidentDisplayName } from 'src/utils/residentUtils';

export interface ResidentProps extends ResidentAvatarProps {
    unit?: Unit;
    hospiceStatus?: HospiceStatus;
    showCareType?: boolean;
}

function Resident(props: ResidentProps) {
    const { resident, unit, hospiceStatus = HospiceStatus.UNKNOWN, showCareType = false } = props;

    const { canSeePHI } = useUserPermissions();

    const residentName = getResidentDisplayName(resident);

    const resuscitationStrategy = resident?.healthInfo?.resuscitationStrategy as
        | ResuscitationStrategyType
        | undefined;
    const hasResuscitationOrder =
        resuscitationStrategy === ResuscitationStrategies.CPR ||
        resuscitationStrategy === ResuscitationStrategies.DNR;

    const subTitle = useMemo(() => {
        const unitText = unit?.displayName || 'No Unit';

        return showCareType && unit?.careType
            ? `${CareTypeLabelsAbbreviated[unit.careType]} | ${unitText}`
            : unitText;
    }, [showCareType, unit]);

    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, width: '100%' }}>
            <ResidentAvatar {...props} />

            <Stack sx={{ gap: 0.5 }}>
                <Typography
                    variant="body1"
                    component="h4"
                    data-dd-privacy="mask"
                    sx={{ fontWeight: 'bold', color: 'grey.900' }}
                >
                    {residentName}
                </Typography>
                <Typography variant="subtitle1" component="p" sx={{ color: 'grey.900' }}>
                    {subTitle}
                </Typography>
            </Stack>

            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    flexGrow: 1,
                }}
            >
                {canSeePHI && isHospice(hospiceStatus) && (
                    <HospiceChip hospiceStatus={hospiceStatus} hideLabel />
                )}

                {canSeePHI && hasResuscitationOrder && (
                    <ResuscitationChip resuscitationStrategy={resuscitationStrategy} />
                )}
            </Stack>
        </Stack>
    );
}

export default Resident;
